import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, TrendingUp, TrendingDown, Scale, Calculator, Calendar } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

const PRESETS = [
  { id: "anno", label: "Tutto l'anno" },
  { id: "t1", label: "1° Trim." },
  { id: "t2", label: "2° Trim." },
  { id: "t3", label: "3° Trim." },
  { id: "t4", label: "4° Trim." },
  { id: "custom", label: "Personalizzato" },
];

export default function AnalisiIva() {
  const currentYear = new Date().getFullYear();
  const [periodo, setPeriodo] = useState("anno");
  const [anno, setAnno] = useState(currentYear);
  const [dataDa, setDataDa] = useState(`${currentYear}-01-01`);
  const [dataA, setDataA] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { periodo, anno };
      if (periodo === "custom") {
        params.data_da = dataDa;
        params.data_a = dataA;
      }
      const { data: res } = await api.get("/fatture/analisi-iva", { params });
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel calcolo IVA");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (periodo !== "custom") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo, anno]);

  // Range anni selezionabili: corrente ± 4
  const yearOptions = Array.from({ length: 7 }, (_, i) => currentYear - 4 + i);

  const saldo = data?.saldo_iva ?? 0;
  const saldoStato = data?.saldo_stato || "pareggio";
  const saldoColor =
    saldoStato === "da_versare"
      ? "text-red-600 dark:text-red-400"
      : saldoStato === "a_credito"
      ? "text-emerald-600 dark:text-emerald-400"
      : "text-muted-foreground";
  const saldoBg =
    saldoStato === "da_versare"
      ? "border-red-500/40 bg-red-500/10"
      : saldoStato === "a_credito"
      ? "border-emerald-500/40 bg-emerald-500/10"
      : "border-border";
  const saldoLabel =
    saldoStato === "da_versare"
      ? "IVA da versare"
      : saldoStato === "a_credito"
      ? "IVA a credito (riportabile)"
      : "IVA in pareggio";

  return (
    <div className="space-y-5" data-testid="analisi-iva-view">
      {/* Barra filtri */}
      <Card className="card-elevated border-primary/20">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 text-primary">
            <Calculator className="h-4 w-4" />
            <div className="text-[10px] uppercase tracking-widest font-bold">
              Periodo di liquidazione
            </div>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            {PRESETS.map((p) => (
              <Button
                key={p.id}
                size="sm"
                variant={periodo === p.id ? "default" : "outline"}
                onClick={() => setPeriodo(p.id)}
                data-testid={`iva-preset-${p.id}`}
                className={`rounded-full h-8 px-4 text-xs font-semibold ${
                  periodo === p.id ? "bg-primary text-primary-foreground" : ""
                }`}
              >
                {p.label}
              </Button>
            ))}

            <div className="ml-2 flex items-center gap-2 border-l border-border pl-3">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Anno
              </Label>
              <select
                data-testid="iva-anno"
                value={anno}
                onChange={(e) => setAnno(parseInt(e.target.value, 10))}
                className="h-8 rounded-md border border-input bg-background px-2 text-xs font-semibold"
                disabled={periodo === "custom"}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {periodo === "custom" && (
            <div className="flex flex-wrap gap-3 items-end pt-2 border-t border-dashed border-border">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Da data
                </Label>
                <Input
                  type="date"
                  value={dataDa}
                  onChange={(e) => setDataDa(e.target.value)}
                  className="h-8 w-40 text-xs"
                  data-testid="iva-data-da"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  A data
                </Label>
                <Input
                  type="date"
                  value={dataA}
                  onChange={(e) => setDataA(e.target.value)}
                  className="h-8 w-40 text-xs"
                  data-testid="iva-data-a"
                />
              </div>
              <Button
                size="sm"
                className="h-8 bg-primary hover:bg-primary/90"
                onClick={load}
                disabled={loading}
                data-testid="iva-apply-custom"
              >
                {loading ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Calendar className="h-3 w-3 mr-1" />
                )}
                Calcola
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Etichetta periodo attivo */}
      {data && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm">
            <span className="text-muted-foreground">Periodo analizzato: </span>
            <span className="font-bold text-foreground" data-testid="iva-periodo-label">
              {data.periodo_label}
            </span>
            <span className="text-muted-foreground text-xs ml-2">
              ({data.data_da} → {data.data_a})
            </span>
          </div>
          <div className="text-xs text-muted-foreground">
            {data.count_vendite} fatture di vendita · {data.count_acquisti} fatture di acquisto
          </div>
        </div>
      )}

      {/* 3 card riepilogo */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* IVA a Debito */}
        <Card className="card-elevated border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-transparent">
          <CardContent className="p-5 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                IVA a Debito
              </div>
              <TrendingUp className="h-4 w-4 text-amber-600" />
            </div>
            <div
              className="text-3xl font-extrabold text-amber-700 dark:text-amber-400 tabular-nums"
              data-testid="iva-debito-value"
            >
              {loading ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                fmt(data?.iva_debito || 0)
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              Da fatture di vendita emesse
              {data?.imponibile_vendite ? (
                <span className="block">
                  Imponibile: {fmt(data.imponibile_vendite)}
                </span>
              ) : null}
            </div>
          </CardContent>
        </Card>

        {/* IVA a Credito */}
        <Card className="card-elevated border-sky-500/30 bg-gradient-to-br from-sky-500/5 to-transparent">
          <CardContent className="p-5 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                IVA a Credito
              </div>
              <TrendingDown className="h-4 w-4 text-sky-600" />
            </div>
            <div
              className="text-3xl font-extrabold text-sky-700 dark:text-sky-400 tabular-nums"
              data-testid="iva-credito-value"
            >
              {loading ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                fmt(data?.iva_credito || 0)
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              Da fatture di acquisto registrate
              {data?.imponibile_acquisti ? (
                <span className="block">
                  Imponibile: {fmt(data.imponibile_acquisti)}
                </span>
              ) : null}
            </div>
          </CardContent>
        </Card>

        {/* Saldo IVA */}
        <Card className={`card-elevated border-2 ${saldoBg}`}>
          <CardContent className="p-5 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                Saldo IVA
              </div>
              <Scale className={`h-4 w-4 ${saldoColor}`} />
            </div>
            <div
              className={`text-3xl font-extrabold tabular-nums ${saldoColor}`}
              data-testid="iva-saldo-value"
            >
              {loading ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                fmt(Math.abs(saldo))
              )}
            </div>
            <div
              className={`text-xs font-bold uppercase tracking-wide ${saldoColor}`}
              data-testid="iva-saldo-stato"
            >
              {saldoLabel}
            </div>
            <div className="text-[10px] text-muted-foreground">
              Debito − Credito = {fmt(saldo)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Note metodologia */}
      <Card className="border-dashed">
        <CardContent className="p-4 text-xs text-muted-foreground space-y-1">
          <div className="font-bold uppercase tracking-widest text-[10px] text-foreground">
            Nota metodologica
          </div>
          <div>
            Il calcolo aggrega l'IVA di tutte le fatture del periodo per <b>data documento</b>.
            L'IVA a credito considera solo le <b>fatture di acquisto</b> (i costi non ancora fatturati o DDT aperti sono esclusi).
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
